import { useEffect, useRef, useState } from 'react';
import { API } from '../service/ProductsService';
import { favorites, useFavoriteIds, parseFavorites, DROP_BADGE } from '../utils/favoritesStore';
import { RUN_SEARCH_EVENT } from '../utils/utils';
import ProductRow from './ProductRow';

// החלון שנפתח בלחיצה על מספר בעמוד הפרופיל (ProfileTab.jsx).
// type: 'favorites' (spGetFavorites - שורות מוצר עם מחירים) | 'searches' | 'priceReports' | 'feedback' (spGetMyActivity, 50 בכל פעם).
// onClose - כפתור ×, Escape, לחיצה על הרקע.

export const PROFILE_LISTS = {
  favorites:    { title: 'המוצרים המועדפים שלי', empty: 'עוד אין מוצרים מועדפים. לוחצים על 🤍 ליד מוצר כדי לשמור אותו' },
  searches:     { title: 'החיפושים שלי',        empty: 'עוד לא חיפשת באתר' },
  priceReports: { title: 'עדכוני המחיר שלי',    empty: 'עוד לא עדכנת מחירים. אפשר לעדכן מחיר בכפתור "עדכון מחיר" ליד כל חנות' },
  feedback:     { title: 'הפידבקים ששלחת',      empty: 'עוד לא שלחת פידבק' },
};

const pad = (n) => String(n).padStart(2, '0');
const formatTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const formatPrice = (n) => `₪${Number(n || 0).toFixed(2)}`;

const s = {
  overlay:  { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '4vh 12px', zIndex: 100 },
  dialog:   { width: '100%', maxWidth: 1000, maxHeight: '92vh', display: 'flex', flexDirection: 'column', background: '#f4f5f7', borderRadius: 14, boxShadow: '0 16px 48px rgba(0,0,0,.25)', overflow: 'hidden' },
  narrow:   { maxWidth: 680 },
  header:   { background: '#fff', padding: '16px 18px 12px', borderBottom: '1px solid #e5e7eb' },
  titleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  title:    { margin: 0, fontSize: 20, fontWeight: 700 },
  count:    { margin: '2px 0 0', fontSize: 13, color: '#59636e' },
  close:    { background: 'none', border: 'none', fontSize: 26, lineHeight: 1, color: '#59636e', padding: '0 4px' },
  body:     { overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 8 },
  message:  { textAlign: 'center', color: '#59636e', padding: '32px 16px', lineHeight: 1.7 },
  error:    { background: '#fff1f0', border: '1px solid #ffccc7', color: '#a8071a', padding: '10px 14px', borderRadius: 10 },
  more:     { alignSelf: 'center', margin: '6px 0 10px', padding: '10px 22px', border: '1px solid #1a3f9c', borderRadius: 10, background: '#fff', color: '#1a3f9c', fontWeight: 600 },
  // שורה כללית (חיפוש / עדכון מחיר / פידבק)
  row:      { display: 'flex', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '10px 14px' },
  icon:     { fontSize: 20, flex: '0 0 auto' },
  main:     { flex: 1, minWidth: 0 },
  primary:  { fontWeight: 600, overflowWrap: 'anywhere' },
  meta:     { fontSize: 13, color: '#59636e', marginTop: 2 },
  time:     { fontSize: 12, color: '#59636e', whiteSpace: 'nowrap' },
  side:     { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flex: '0 0 auto' },
  again:    { padding: '5px 12px', fontSize: 13, fontWeight: 600, border: '1px solid #1a3f9c', borderRadius: 8, background: '#fff', color: '#1a3f9c', whiteSpace: 'nowrap' },
  tag:      { fontSize: 12, fontWeight: 600, borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap' },
  tagOn:    { color: '#1a7f37', background: '#effaf2' },
  tagOff:   { color: '#59636e', background: '#f6f8fa' },
  message2: { whiteSpace: 'pre-wrap', lineHeight: 1.6, overflowWrap: 'anywhere' },
  unavail:  { fontSize: 14, fontWeight: 700, color: '#59636e', margin: '10px 0 0' },
};

const SearchItem = ({ row, onRun }) => (
  <div style={s.row}>
    <span style={s.icon} aria-hidden="true">🔍</span>
    <div style={s.main}>
      <div style={s.primary}>{row.q}</div>
      <div style={s.meta}>{row.resultCount != null ? `${Number(row.resultCount).toLocaleString('he-IL')} תוצאות` : ''}</div>
    </div>
    <div style={s.side}>
      <span style={s.time} className="num">{formatTime(row.iTime)}</span>
      {row.q && <button type="button" style={s.again} onClick={() => onRun(row.q)}>חיפוש שוב</button>}
    </div>
  </div>
);

const PriceReportItem = ({ row }) => (
  <div style={s.row}>
    <span style={s.icon} aria-hidden="true">🏷️</span>
    <div style={s.main}>
      <div style={s.primary}>{row.productName}</div>
      <div style={s.meta}>
        {row.storeName} · עדכנת ל-<b className="num">{formatPrice(row.price)}</b>
        {row.sitePrice != null && <> (באתר החנות: <span className="num">{formatPrice(row.sitePrice)}</span>)</>}
      </div>
    </div>
    <div style={s.side}>
      <span style={s.time} className="num">{formatTime(row.iTime)}</span>
      <span style={{ ...s.tag, ...(row.isCurrent ? s.tagOn : s.tagOff) }}
            title={row.isCurrent ? 'זה המחיר שמוצג היום באתר' : 'המחיר עודכן מאז - ע"י משתמש אחר או באתר החנות'}>
        {row.isCurrent ? 'בתוקף' : 'עודכן מאז'}
      </span>
    </div>
  </div>
);

const FeedbackItem = ({ row }) => (
  <div style={{ ...s.row, alignItems: 'flex-start' }}>
    <span style={s.icon} aria-hidden="true">💬</span>
    <div style={s.main}><div style={s.message2}>{row.message}</div></div>
    <div style={s.side}>
      <span style={s.time} className="num">{formatTime(row.iTime)}</span>
      <span style={{ ...s.tag, ...(row.isRead ? s.tagOn : s.tagOff) }}>{row.isRead ? '✓ נקרא ע"י הצוות' : 'נשלח'}</span>
    </div>
  </div>
);

const ProfileListModal = ({ type, onClose }) => {
  const config = PROFILE_LISTS[type];
  const isFavorites = type === 'favorites';
  const favoriteIds = useFavoriteIds();
  const [rows, setRows] = useState([]);            // activity: השורות; favorites: המוצרים
  const [fav, setFav] = useState(null);            // favorites: { stores, pricesByItem, unavailable }
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestId = useRef(0);

  // נועל את גלילת העמוד שמאחורי החלון, ו-Escape סוגר
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const load = async (pageNum) => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const resp = isFavorites ? await API.getFavorites() : await API.getMyActivity(type, pageNum);
      if (id !== requestId.current) return;
      const status = resp?.[0]?.[0];
      if (status?.code !== 0) {
        if (status?.code !== 90) setError(status?.msg || 'שגיאה בטעינת הרשימה');
        return;
      }
      if (isFavorites) {
        // אותו מבנה כמו החיפוש + [4] לא נמכרים כרגע + [5] ירד מחיר
        const data = parseFavorites(resp);
        setFav(data);
        setRows(data.items);
      } else {
        const newRows = resp[2] || [];
        setTotalCount(resp[1]?.[0]?.totalCount ?? 0);
        setRows((prev) => (pageNum > 0 ? [...prev, ...newRows] : newRows));
        setPage(pageNum);
      }
    } catch (err) {
      if (id === requestId.current) setError(err.message || 'שגיאה בטעינת הרשימה');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  useEffect(() => { load(0); }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  // "חיפוש שוב" - MainPage עובר לטאב החיפוש ו-SearchTab מריץ את החיפוש
  const runSearch = (q) => {
    window.dispatchEvent(new CustomEvent(RUN_SEARCH_EVENT, { detail: q }));
    onClose();
  };

  // מועדפים: הסרה ב-❤ מעלימה את המוצר מיד
  const stillFavorite = (x) => !favorites.isLoaded() || favoriteIds.has(x.id);
  const dropsByItem = fav?.dropsByItem || {};
  const favItems = isFavorites
    ? rows.filter(stillFavorite).sort((a, b) => (dropsByItem[b.id] ? 1 : 0) - (dropsByItem[a.id] ? 1 : 0))
    : [];
  const unavailable = isFavorites && fav ? fav.unavailable.filter(stillFavorite) : [];
  const shownCount = isFavorites ? favItems.length + unavailable.length : totalCount;
  const isEmpty = !loading && !error && (isFavorites ? shownCount === 0 : rows.length === 0);
  const hasMore = !isFavorites && rows.length < totalCount;

  return (
    <div style={s.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...s.dialog, ...(isFavorites ? null : s.narrow) }} role="dialog" aria-modal="true" aria-labelledby="profile-list-title">
        <div style={s.header}>
          <div style={s.titleRow}>
            <div>
              <h2 id="profile-list-title" style={s.title}>{config.title}</h2>
              <p style={s.count}>{loading && !rows.length ? 'טוען…' : `${shownCount.toLocaleString('he-IL')} סה"כ`}</p>
            </div>
            <button style={s.close} onClick={onClose} aria-label="סגירה">×</button>
          </div>
        </div>

        <div style={s.body}>
          {error && <div style={s.error} role="alert">{error}</div>}
          {isEmpty && <div style={s.message}>{config.empty}</div>}

          {isFavorites && fav && favItems.map((item) => (
            <ProductRow key={item.id} item={item} stores={fav.stores} prices={fav.pricesByItem[item.id] || {}}
                        badge={dropsByItem[item.id] ? DROP_BADGE : null} drops={dropsByItem[item.id]} />
          ))}
          {unavailable.length > 0 && (
            <>
              <h3 style={s.unavail}>לא נמכרים כרגע באף חנות</h3>
              {unavailable.map((x) => (
                <div key={x.id} style={s.row}>
                  <div style={s.main}><div style={s.primary}>{x.name || `מוצר ${x.id}`}</div></div>
                  <button type="button" style={s.again} onClick={() => favorites.set(x.id, false)}>הסרה</button>
                </div>
              ))}
            </>
          )}

          {type === 'searches' && rows.map((row) => <SearchItem key={row.id} row={row} onRun={runSearch} />)}
          {type === 'priceReports' && rows.map((row) => <PriceReportItem key={row.id} row={row} />)}
          {type === 'feedback' && rows.map((row) => <FeedbackItem key={row.id} row={row} />)}

          {hasMore && (
            <button style={s.more} onClick={() => load(page + 1)} disabled={loading}>
              {loading ? 'טוען…' : `טען עוד (${(totalCount - rows.length).toLocaleString('he-IL')} נוספים)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileListModal;
